import React from "react";
import Konva from "konva";
import { layerRefAtom } from "state-atoms";

import { useBoardValue } from "frontend/state/board-state";
import { useHarmonicIntervalFn, useUpdate } from "react-use";
import { useAtom, useAtomValue } from "jotai";

import { VotingSession } from "shared/datamodel/schemas/voting-session";
import StyledButton, { ButtonStyle } from "frontend/ui-components/styled-button";
import { formatVotesLeft, votingAtom, secondsLeft, sumValuesOfObject } from "../voting-common";
import style from "./voting-panel.module.css";

import { ElementThumbnail } from "./components/element-thumbnail";

export function VotingOngoing({
  session,
  stillNotDone,
  onEndForMe,
  onEndForAll,
}: {
  session: VotingSession;
  stillNotDone: boolean;
  onEndForMe: () => void;
  onEndForAll: () => void;
}) {
  const konvaLayer = useAtomValue(layerRefAtom)?.current ?? null;

  const [{ user }] = useBoardValue();
  const myid = user?.id; // is this the same as userAtom.id? user context ?
  const mySession = session.ownerId == myid;
  const [state] = useAtom(votingAtom);

  const myVotes = state!.votes;
  const total = sumValuesOfObject(myVotes);
  const votesRemaining = session.numVotes - total;

  const votesLeftMessage = formatVotesLeft(votesRemaining);

  return (
    <div className={style.ongoing}>
      <div className={style.topic}>{session.title.replace("/n", "<br/>")}</div>

      {stillNotDone && (
        <div className={style.votesLeft}>
          <span>{votesLeftMessage}</span>
          <StyledButton title="I'm done" onClick={onEndForMe} />
        </div>
      )}

      <VoteLiveResults konvaLayer={konvaLayer} votes={myVotes} />

      <div className={style.endZone}>
        <VoteSessionTimer session={session} />
        {mySession && secondsLeft(session!)! > 10 && (
          <StyledButton title="End for all" style={ButtonStyle.outline} onClick={onEndForAll} />
        )}
      </div>
    </div>
  );
}

export function VoteLiveResults({
  konvaLayer,
  votes,
}: {
  konvaLayer: Konva.Layer | null;
  votes: Record<string, number>; // count of votes on object
}) {
  const votesSortedDescending = Object.entries(votes).sort((a, b) => b[1] - a[1]);
  const finalList = votesSortedDescending.filter(([, numberVotes]) => numberVotes > 0);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: "1",
        minHeight: "0",
        overflowY: "auto",
        placeItems: "flex-start",
      }}
    >
      {finalList.map(([itemId, numberVotes]) => (
        <div key={itemId} className={style.itemResultContainer}>
          <ElementThumbnail konvaLayer={konvaLayer} objId={itemId} />
          <span className={style.number}>{numberVotes}</span>votes
        </div>
      ))}
    </div>
  );
}

function VoteSessionTimer({ session }: { session: VotingSession }) {
  useHarmonicIntervalFn(useUpdate(), 1000);
  const left = secondsLeft(session) || -1;
  if (left <= 0) return null;
  const rounded = Math.ceil(left);
  const seconds = rounded % 60;
  const minutes = Math.floor(rounded / 60);
  const secondsStr = seconds.toString().padStart(2, "0");
  const minutesStr = minutes.toString().padStart(2, "0");

  return (
    <>
      <span className={style.h2}>Countdown</span>
      <span className={style.countdownInput}>
        {minutesStr}:{secondsStr}
      </span>
    </>
  );
}
